import { readStorage, writeStorage } from "./browserStorage.js";
import { leadRegionGroups } from "./leadRegions.js";
import { formatLeadPhone } from "./leadInput.js";

import "./families/home.css";
import "./families/general.css";
import "./families/legal.css";
import "./families/hub.css";
import "./families/comparison.css";
import "./families/amount.css";
import "./families/productLocation.css";
import "./families/localIndustry.css";
import "./families/guide.css";
import "./families/standalone.css";
import "./families/article.css";
import PaymentCalculator from "./PaymentCalculator.jsx";
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import "./header.css";
import "./forms.css";
import "./legal.css";
import "./directory.css";
import "./comparison.css";
import "./amount.css";
import "./product-location.css";
import "./local-industry.css";
import "./guide.css";
import "./standalone.css";
import AnalyticsTracker from "./analytics/AnalyticsTracker.jsx";
import ConsentBanner from "./analytics/ConsentBanner.jsx";
// Generated public pages never download the development/fallback renderer.
let PublicPage, titleFor, descriptionFor;
const loadPageData = () => import("./publicFallback.jsx").then(({ createPublicRenderer }) => {
  ({ PublicPage, titleFor, descriptionFor } = createPublicRenderer({ site, LeadForm, PaymentCalculator }));
});

const site = {
  name: "AIDBIZ",
  accent: "Small business capital, delivered with precision.",
  phone: "+1 (929) 744-5992",
  phoneE164: "+19297445992",
  contactEmail: "info@aidbizfunding.com",
  disclosure: "AIDBIZ is a team of small-business funding specialists. Offers depend on revenue, underwriting, verification, and funding partner terms. Approval speed, cost, renewal eligibility, and funding amount vary by applicant.",
  api: import.meta.env.VITE_PUBLIC_API_URL,
};
const Admin = lazy(() => import("./admin/Admin.jsx"));
const Application = lazy(() => import("./Application.jsx"));
const canonicalPath = () =>
  decodeURIComponent(window.location.pathname).replace(/\.html$/, "").replace(/\/$/, "") || "/";
function LeadForm({ defaults = {} }) {
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");
  const statusRef = useRef(null);
  const nameRef = useRef(null);
  const restartRef = useRef(false);
  useEffect(() => {
    if (["sent", "error", "setup"].includes(state)) statusRef.current?.focus();
    else if (state === "idle" && restartRef.current) {
      restartRef.current = false;
      nameRef.current?.focus();
    }
  }, [state]);
  async function submit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = Object.fromEntries(new FormData(form));
    fields.sourcePath = canonicalPath();
    fields.consent = fields.consent === "yes";
    const query = new URLSearchParams(location.search);
    // landingUrl/referrer/campaign describe where the visitor entered this tab; sourcePath is the submission page.
    const entry = entryAttribution();
    const tag = key => entry?.[key] || query.get({ utmSource: "utm_source", utmMedium: "utm_medium", utmCampaign: "utm_campaign", utmContent: "utm_content", utmTerm: "utm_term", gclid: "gclid", fbclid: "fbclid" }[key]);
    fields.attribution = {
      landingUrl: entry?.landingUrl || `${location.origin}${location.pathname}`,
      referrer: entry ? entry.referrer : document.referrer || null,
      utmSource: tag("utmSource"),
      utmMedium: tag("utmMedium"),
      utmCampaign: tag("utmCampaign"),
      utmContent: tag("utmContent"),
      utmTerm: tag("utmTerm"),
      gclid: tag("gclid"),
      fbclid: tag("fbclid"),
      deviceType: innerWidth < 640 ? "mobile" : innerWidth < 1024 ? "tablet" : "desktop",
      screenSize: `${screen.width}x${screen.height}`,
    };
    if (
      readStorage("localStorage", "aidbiz_analytics_consent") === "granted" &&
      navigator.doNotTrack !== "1" &&
      navigator.globalPrivacyControl !== true
    ) {
      fields.analytics = {
        consent: "granted",
        visitorId: readStorage("localStorage", "aidbiz_analytics_visitor"),
        sessionId: readStorage("sessionStorage", "aidbiz_analytics_session"),
        landingPath: readStorage("sessionStorage", "aidbiz_analytics_landing"),
        referrer: document.referrer || null,
        deviceType:
          innerWidth < 640
            ? "mobile"
            : innerWidth < 1024
              ? "tablet"
              : "desktop",
        utmSource: query.get("utm_source"),
        utmMedium: query.get("utm_medium"),
        utmCampaign: query.get("utm_campaign"),
        utmContent: query.get("utm_content"),
        utmTerm: query.get("utm_term"),
      };
    }
    if (fields.analytics && (!fields.analytics.visitorId || !fields.analytics.sessionId || !fields.analytics.landingPath)) delete fields.analytics;
    if (!site.api) {
      setState("setup");
      return;
    }
    setState("sending");
    setMessage("");
    try {
      const response = await fetch(`${site.api}/submit-lead`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(fields),
        signal: AbortSignal.timeout(45000),
      });
      const result = await response.json().catch(() => ({}));
      setMessage(typeof result.error === "string" ? result.error : "");
      if (response.ok) {
        form.reset();
        setState("sent");
      } else setState("error");
    } catch {
      setMessage("We couldn’t confirm whether your request arrived. Your details are still here. You can try again or call us for help.");
      setState("error");
    }
  }
  if (state === "sent") return <section className="lead-confirmation" role="status" aria-live="polite" tabIndex={-1} ref={statusRef}>
    <h3>Your funding request was received</h3>
    <p>Check your email, including spam, for the next step and a private application link. If it does not arrive, call <a href={"tel:" + site.phoneE164}>{site.phone}</a>.</p>
    <p>This confirms receipt of your request. Funding approval and terms depend on review.</p>
    <button type="button" className="quiet" onClick={() => { restartRef.current = true; setState("idle"); setMessage(""); }}>Start another request</button>
  </section>;
  return (
    <form
      className="lead-form"
      data-analytics-form="lead_form"
      onSubmit={submit}
      onInvalidCapture={event => {
        const details = event.target.closest("details");
        if (details) details.open = true;
      }}
      aria-busy={state === "sending"}
    >
      <div className="form-grid">
        <label>Full name<input ref={nameRef} required name="contactName" autoComplete="name" minLength="2" maxLength="120" /></label>
        <label>Business name<input required name="businessName" autoComplete="organization" minLength="2" maxLength="160" /></label>
        <label>Work email<input required name="email" type="email" autoComplete="email" maxLength="254" /></label>
        <label>Phone<input required name="phone" type="tel" autoComplete="tel" inputMode="tel" placeholder="(555) 555-0123" maxLength="40" pattern="\(\d{3}\) \d{3}-\d{4}" title="Enter a 10-digit US or Canadian phone number" onInput={e => { e.target.value = formatLeadPhone(e.target.value); }} /></label>
        <label>State / province<select required name="businessState" autoComplete="address-level1" defaultValue={defaults.businessState || ""}><option value="" disabled>Select state or province</option>{leadRegionGroups.map(group => <optgroup key={group.label} label={group.label}>{group.options.map(([code, name]) => <option key={code} value={code}>{name} ({code})</option>)}</optgroup>)}</select></label>
        <label>Funding amount<select required name="fundingNeed" defaultValue=""><option value="" disabled>Select amount</option><option>$5,000–$25,000</option><option>$25,000–$75,000</option><option>$75,000–$150,000</option><option>$150,000–$500,000</option><option>$500,000–$1M</option><option>$1M+</option></select></label>
      </div>
      <details className="lead-optional"><summary>Add business details (optional)</summary><div className="form-grid">
        <label className="wide">Business street address<input name="businessAddress" autoComplete="street-address" maxLength="200" /></label>
        <label>City<input name="businessCity" autoComplete="address-level2" maxLength="120" defaultValue={defaults.businessCity || ""} /></label>
        <label>ZIP / postal code<input name="businessZip" autoComplete="postal-code" pattern="[0-9]{5}(-[0-9]{4})?|[A-Za-z][0-9][A-Za-z] ?[0-9][A-Za-z][0-9]" placeholder="10001" /></label>
        <label>Industry<select name="industry" defaultValue={defaults.industry || ""}><option value="" disabled>Select industry</option><option>Restaurant / Food Service</option><option>Healthcare / Medical / Dental</option><option>Construction / Contracting</option><option>Retail Store / E-commerce</option><option>Salon / Beauty / Spa</option><option>Auto Repair / Services</option><option>Professional Services</option><option>Other</option></select></label>
        <label>Average monthly revenue<select name="monthlyRevenue" defaultValue=""><option value="" disabled>Select revenue</option><option>Under $10,000 / month</option><option>$10,000–$25,000 / month</option><option>$25,000–$50,000 / month</option><option>$50,000–$100,000 / month</option><option>$100,000–$250,000 / month</option><option>$250,000+ / month</option></select></label>
      </div></details>
      <label className="consent"><input required name="consent" type="checkbox" value="yes" /> <span>I agree that AIDBIZ may contact me by phone, email, or text about business funding options, including through automated technology where permitted. Message and data rates may apply. Consent is not a condition of purchase. See our <a href="/privacy">Privacy Notice</a> and <a href="/terms">Terms</a>.</span></label>
      <label className="honeypot" aria-hidden="true">
        Website
        <input name="website" tabIndex="-1" autoComplete="off" />
      </label>
      <button
        data-analytics-action="lead_form_submit"
        disabled={state === "sending"}
      >
        {state === "sending" ? "Sending…" : site.cta || "Start funding review"}
      </button>
      {state === "setup" && (
        <p className="hint" role="alert" tabIndex={-1} ref={statusRef}>Online requests are temporarily unavailable. Please call <a href={"tel:" + site.phoneE164}>{site.phone}</a> for help.</p>
      )}
      {state === "error" && (
        <p className="error" role="alert" tabIndex={-1} ref={statusRef}>{message || "We couldn’t send that yet. Please try again."}</p>
      )}
    </form>
  );
}
function App() {
  const path = canonicalPath();
  const [, setBrandRevision] = useState(0);
  const privateRoute =
    path === "/admin" ||
    path.startsWith("/admin/") ||
    path.startsWith("/apply/");
  useEffect(() => {
    document.title = privateRoute
      ? `Secure portal | ${site.name}`
      : titleFor(path);
    let tag = document.querySelector('link[rel="canonical"]');
    if (!tag) {
      tag = document.createElement("link");
      tag.rel = "canonical";
      document.head.append(tag);
    }
    tag.href = privateRoute
      ? `${window.location.origin}${path.startsWith("/apply/") ? "/apply" : "/admin"}`
      : `${window.location.origin}${path}`;
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.name = "description";
      document.head.append(description);
    }
    description.content = privateRoute
      ? "Secure AIDBIZ portal."
      : descriptionFor(path);
    let robots = document.querySelector('meta[name="robots"]');
    if (privateRoute) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.name = "robots";
        document.head.append(robots);
      }
      robots.content = "noindex, nofollow, noarchive";
      let referrer = document.querySelector('meta[name="referrer"]');
      if (!referrer) {
        referrer = document.createElement("meta");
        referrer.name = "referrer";
        document.head.append(referrer);
      }
      referrer.content = "no-referrer";
    } else if (robots) {
      robots.content = "index, follow";
    }
  }, [path, privateRoute]);
  useEffect(()=>{if(privateRoute||!site.api)return;fetch(`${site.api}/public-settings`).then(response=>response.ok?response.json():null).then(result=>{if(!result?.settings)return;site.name=result.settings.display_name||site.name;site.accent=result.settings.tagline||site.accent;site.cta=result.settings.cta_text;site.contactEmail=result.settings.contact_email;site.phone=result.settings.display_phone;site.disclosure=result.settings.funding_disclosure;setBrandRevision(value=>value+1);}).catch(()=>undefined);},[privateRoute]);
  if (path === "/admin" || path.startsWith("/admin/"))
    return (
      <Suspense
        fallback={
          <main className="application">
            <p>Loading secure admin…</p>
          </main>
        }
      >
        <Admin />
      </Suspense>
    );
  return path.startsWith("/apply/") ? (
    <Suspense fallback={<main className="application"><p>Loading secure application…</p></main>}>
      <Application api={site.api} secret={path.slice("/apply/".length)} />
    </Suspense>
  ) : (
    <>
      <AnalyticsTracker path={path} />
      <ConsentBanner />
      <PublicPage path={path} />
    </>
  );
}
// Entry attribution (2026-10-14): remember the first page of this browser tab, its outside referrer and campaign tags,
// so a lead submitted after internal navigation still reports where the visitor arrived. Tab-only (sessionStorage), sent
// only with a form the visitor chooses to submit, and skipped entirely under Do Not Track or Global Privacy Control.
const ENTRY_KEY = "aidbiz_entry";
const privacySignal = () => navigator.doNotTrack === "1" || navigator.globalPrivacyControl === true;
try {
  if (!privacySignal() && !readStorage("sessionStorage", ENTRY_KEY)) {
    const q = new URLSearchParams(location.search);
    const externalReferrer = document.referrer && !document.referrer.startsWith(location.origin) ? document.referrer : null;
    writeStorage("sessionStorage", ENTRY_KEY, JSON.stringify({ landingUrl: `${location.origin}${location.pathname}`, referrer: externalReferrer, utmSource: q.get("utm_source"), utmMedium: q.get("utm_medium"), utmCampaign: q.get("utm_campaign"), utmContent: q.get("utm_content"), utmTerm: q.get("utm_term"), gclid: q.get("gclid"), fbclid: q.get("fbclid") }));
  }
} catch { /* storage unavailable: fall back to the current page */ }
const entryAttribution = () => { if (privacySignal()) return null; try { return JSON.parse(readStorage("sessionStorage", ENTRY_KEY) || "null"); } catch { return null; } };
const rootElement=document.getElementById("root");
const bootPath=canonicalPath();
const bootPrivate=bootPath==="/admin"||bootPath.startsWith("/admin/")||bootPath.startsWith("/apply/");
const staticPage=!bootPrivate&&Boolean(rootElement.querySelector("main"));
if(!staticPage){
  // Private routes (and any page without pre-rendered HTML, e.g. the dev server) render the full React app.
  const appRoot=import.meta.hot?(globalThis.__aidbizReactRoot??=createRoot(rootElement)):createRoot(rootElement);
  if (bootPrivate) appRoot.render(<App />); else loadPageData().then(() => appRoot.render(<App />));
}else{
  // Public pages are static HTML; React mounts only the interactive islands so content is single-sourced in the generator.
  // The generator supplies only this page's optional form defaults, outside the visible content.
  let defaults = {};
  try { defaults = JSON.parse(rootElement.dataset.leadDefaults || "{}"); } catch { /* empty defaults remain usable */ }
  const formHost=rootElement.querySelector("#get-started");
  if(formHost&&!formHost.querySelector("form")){const mount=document.createElement("div");mount.className="lead-form-island";formHost.appendChild(mount);createRoot(mount).render(<LeadForm defaults={defaults}/>);}
  const menuToggle=rootElement.querySelector(".menu-toggle"),primaryNav=rootElement.querySelector("#primary-nav");
  if(menuToggle&&primaryNav)menuToggle.addEventListener("click",()=>{const open=primaryNav.classList.toggle("open");menuToggle.textContent=open?"Close":"Menu";menuToggle.setAttribute("aria-expanded",String(open));});
  const chrome=document.createElement("div");chrome.id="site-islands";document.body.appendChild(chrome);
  createRoot(chrome).render(<><AnalyticsTracker path={bootPath}/><ConsentBanner/></>);
  for(const el of rootElement.querySelectorAll('[data-island="calculator"]')){const host=document.createElement("div");el.appendChild(host);el.querySelector(".calculator-static")?.setAttribute("hidden","");createRoot(host).render(<PaymentCalculator product={el.dataset.product} amount={el.dataset.amount} country={el.dataset.country}/>);}
  if(site.api)fetch(`${site.api}/public-settings`).then(r=>r.ok?r.json():null).then(result=>{const s=result?.settings;if(!s)return;for(const el of rootElement.querySelectorAll("[data-brand=phone]"))if(s.display_phone)el.textContent=s.display_phone;}).catch(()=>undefined);
}
